import { useContext, useEffect } from "react";
import { Popper, Paper, Typography, Fab } from "@material-ui/core";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_INITIAL_CALL_INFO_MINIMUM } from "../../Queries/GetInitialCallInfoMinimum";
import { styled } from "styled-components";
import { SET_OFFICER_INCIDENT_TIMES } from "../../Mutations/SetOfficerIncidentTimes";
import { nowServerFormat } from "../../utils/utils";
import { SET_STATUS } from "../../Mutations/SetStatus";
import { SetupContext } from "../../Contexts/SetupContext";
import { SET_AVAIL_OFFICER } from "../../Mutations/SetAvailOfficer";
import doorbell from "../../sounds/doorbell.wav";
import { withStyles } from "@material-ui/core/styles";
import moment from "moment";

const Header = styled.div`
  background-color: #fae8ad;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 5px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
`;

const StyledSpan = styled.span`
  font-size: 0.75rem;
`;

const StyledPaper = withStyles({
  root: {
    border: "1px solid black",
    paddingBottom: "5px",
    minWidth: "300px",
  },
})(Paper);

const getDispatchableOisId = (officerIncidentSpecifics = [], userId) => {
  if (!userId) return undefined;

  const filtered = officerIncidentSpecifics
    .filter(ois => ois?.officer?.id?.toString() === userId?.toString())
    .filter(ois => ois?.dispatchedAt && !ois?.enrouteAt);
  return filtered.length > 0 ? filtered[filtered.length - 1]?.id : undefined;
};

const DispatchedModal = ({
  open,
  close,
  incidentId,
  userId,
  handleIncidentOpening,
}) => {
  const rootElement = document.getElementById("app");

  const { setup } = useContext(SetupContext);
  const { logCodes } = setup.setupLists || {};
  const [getIncident, { data }] = useLazyQuery(GET_INITIAL_CALL_INFO_MINIMUM);

  const [setOfficerIncidentTime] = useMutation(SET_OFFICER_INCIDENT_TIMES, {
    onError: err => console.log(err),
  });
  const [setStatus] = useMutation(SET_STATUS, {
    onError: err => console.log(err),
  });
  const [setAvailOfficer] = useMutation(SET_AVAIL_OFFICER, {
    onError: err => console.log(err),
  });

  const enrouteHandler = async () => {
    if (!userId || !incidentId) {
      console.error(
        "missing userId or incidentId for enrouteHandler: ",
        userId,
        incidentId,
      );
      return;
    }

    let freshData;
    try {
      const result = await getIncident({
        variables: { originatorId: userId, incidentId },
        fetchPolicy: "network-only",
      });
      freshData = result.data;
    } catch (err) {
      console.error("Error fetching incident in enrouteHandler:", err);
      close();
      return;
    }
    const freshIncident = freshData?.initialCallInfo;
    const oisId = getDispatchableOisId(
      freshIncident?.officerIncidentSpecifics,
      userId,
    );

    if (!userId || !oisId) {
      console.error(
        "no dispatchable ois found for enrouteHandler: ",
        userId,
        oisId,
      );
      close();
      return;
    }

    const enrouteAt = nowServerFormat();
    const callLogEntryText = moment
      .utc(enrouteAt, "YYYY-MM-DD HH:mm:ss UTC")
      .local()
      .format("YYYY-MM-DD HH:mm:ss");

    setOfficerIncidentTime({
      variables: {
        input: {
          originatorId: userId,
          officerIncidentSpecificId: oisId,
          enrouteAt: enrouteAt,
        },
      },
    });

    const logCode = logCodes?.find(lc => lc?.name?.startsWith("76/"));

    if (logCode?.id) {
      setStatus({
        variables: {
          input: {
            originatorId: userId,
            officerId: userId,
            officerIncidentSpecificId: oisId,
            logCodeId: logCode?.id,
            statusCode: logCode?.name?.split("/")?.[0],
            entryText: callLogEntryText,
          },
        },
      });
    } else {
      console.error("no logCodeId sent to enrouteHandler: ", logCode?.id);
    }

    setAvailOfficer({
      variables: {
        input: {
          originatorId: userId,
          officerId: userId,
          secondaryAddress: "",
        },
      },
    });
    handleIncidentOpening(freshIncident?.id, true);
    close();
  };

  useEffect(() => {
    if (incidentId) {
      getIncident({
        variables: { originatorId: userId, incidentId: incidentId },
      });
    }
  }, [getIncident, incidentId, userId]);

  async function playAudio(file) {
    try {
      await new Audio(file).play();
    } catch (err) {
      console.log(err);
    }
  }

  useEffect(() => {
    if (open) {
      playAudio(doorbell);
    }
  }, [open]);

  return (
    <Popper
      placement="top"
      open={open}
      disablePortal={false}
      anchorEl={rootElement}
      modifiers={{
        flip: {
          enabled: false,
        },
        preventOverflow: {
          enabled: true,
          boundariesElement: "viewport",
        },
      }}
      style={{ zIndex: "3" }}
    >
      <StyledPaper>
        <Header>
          <Typography variant="h6">Incident Notification</Typography>
          <button onClick={close}>X</button>
        </Header>
        <Content incident={data?.initialCallInfo} />
        <EnRouteButton enrouteHandler={enrouteHandler} />
      </StyledPaper>
    </Popper>
  );
};

const Content = ({ incident = {} }) => {
  const callType = incident?.classification?.name;
  const address = incident?.address?.name;
  return (
    <FlexColumn>
      <Typography>
          You&apos;ve been dispatched to {incident?.incidentNumber}{" "}
        {callType && `(${callType})`}
      </Typography>
      {address && <Typography> at {address}</Typography>}
    </FlexColumn>
  );
};

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;
const defaultErrorFunction = propName =>
  console.error(`No function passed to ${propName}`);

const EnRouteButton = ({
  enrouteHandler = () => defaultErrorFunction(enrouteHandler),
}) => {
  return (
    <FlexRow>
      <Fab
        size="large"
        style={{ backgroundColor: "#6096d1", color: "white" }}
        onClick={enrouteHandler}
      >
        <StyledSpan>En Route</StyledSpan>
      </Fab>
    </FlexRow>
  );
};

export default DispatchedModal;
export { Content, EnRouteButton, getDispatchableOisId };
